import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as circles from "@/api/circles";

// ═══════════════════════════════════════════════════════════════════
// Circles — friend groups, the plural half of the retention unit.
//
// One household (who you live with), up to ten circles (who you go out with).
// The membership table is unique on the PAIR rather than on the user, which is
// exactly what lets somebody be in the Sunday cricket lot AND the garba group.
//
// Two things this page is built to show:
//
//   · The invite LINK has no recipient. It is meant to be pasted into a group
//     chat, and recording who it was sent to would turn a shareable token into
//     a list of who was asked. Revocable and expiring instead.
//   · Invites are pull-never-force with silent declines — ignoring one tells
//     the sender nothing.
// ═══════════════════════════════════════════════════════════════════

export default function CirclesLab() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [joinToken, setJoinToken] = useState("");
  // The open circle's invites still waiting, once read. Admins only.
  const [sent, setSent] = useState<{ circleId: string; invites: circles.SentCircleInvite[] } | null>(null);

  const say = (m: string) => setLog((l) => [m, ...l].slice(0, 8));

  const { data, isLoading, error } = useQuery({
    queryKey: ["circles"],
    queryFn: circles.listMyCircles,
  });

  const mine = data?.data.circles ?? [];

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      say(await fn());
      await qc.invalidateQueries({ queryKey: ["circles"] });
    } catch (e) {
      say("✘ " + (e instanceof Error ? e.message : "failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Circles"
        subtitle="Friend groups — who you go out with, as opposed to who you live with."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Circles" }]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-1">Make one</h2>
        <p className="text-xs text-gray-500 mb-3">
          Up to ten. The cap is enforced by the server, so the eleventh is
          refused rather than silently dropped.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sunday cricket"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <input
            value={emoji}
            onChange={(e) => setEmoji(e.target.value.slice(0, 2))}
            placeholder="🏏"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-16"
          />
          <Button
            size="sm"
            isLoading={busy}
            disabled={!name.trim()}
            onClick={() =>
              run(async () => {
                const r = await circles.createCircle({
                  name: name.trim(),
                  ...(emoji ? { emoji } : {}),
                });
                setName("");
                setEmoji("");
                return `created "${r.data.name}"`;
              })
            }
          >
            Create
          </Button>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-3">Yours</h2>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : "Could not load circles"}
          </p>
        ) : !mine.length ? (
          <p className="text-sm text-gray-500">
            None yet. A circle is the group you actually make plans with — the
            unit group checkout assigns tickets to.
          </p>
        ) : (
          <ul className="space-y-2">
            {mine.map((c) => (
              <li key={c.id} className="border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {c.emoji && <span>{c.emoji}</span>}
                  <span className="font-medium text-gray-900">{c.name}</span>
                  <Badge variant="gray">{c.memberCount} members</Badge>
                  {c.myRole && <Badge variant="blue">{c.myRole}</Badge>}
                  <button
                    onClick={() => setOpenId(openId === c.id ? null : c.id)}
                    className="ml-auto text-xs text-blue-600 hover:underline"
                  >
                    {openId === c.id ? "hide" : "manage"}
                  </button>
                </div>

                {openId === c.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {sent?.circleId === c.id && sent.invites.length > 0 && (
                      <ul className="space-y-1">
                        {sent.invites.map((inv) => (
                          <li key={inv.id} className="flex items-center gap-2 text-xs text-gray-700">
                            <span>
                              {inv.invitee.name}, invited by {inv.invitedBy.name}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              isLoading={busy}
                              onClick={() =>
                                run(async () => {
                                  await circles.withdrawCircleInvite(c.id, inv.id);
                                  setSent((cur) => cur && { ...cur, invites: cur.invites.filter((i) => i.id !== inv.id) });
                                  return `withdrew the invite to ${inv.invitee.name}, silently`;
                                })
                              }
                            >
                              Withdraw
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            const r = await circles.createCircleInviteLink(c.id);
                            return `link (expires ${new Date(
                              r.data.expiresAt,
                            ).toLocaleDateString()}): ${r.data.url}`;
                          })
                        }
                      >
                        Make invite link
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            await circles.revokeCircleInviteLink(c.id);
                            return "link revoked";
                          })
                        }
                      >
                        Revoke link
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            const r = await circles.getCircle(c.id);
                            return `${r.data.name} (started by ${
                              r.data.createdBy?.name ?? "?"
                            }): ${
                              r.data.members?.map((m) => m.user.name).join(", ") ??
                              "no member list"
                            }`;
                          })
                        }
                      >
                        Read detail
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            const r = await circles.getCircle(c.id);
                            setSent(r.data.sentInvites ? { circleId: c.id, invites: r.data.sentInvites } : null);
                            return r.data.sentInvites === null
                              ? "only an admin sees the invites a circle sent"
                              : `${r.data.sentInvites.length} invite(s) waiting`;
                          })
                        }
                      >
                        Sent invites
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            // What the circle is going to: members' passes and going RSVPs,
                            // naming circle members only.
                            const r = await circles.getCircleEvents(c.id);
                            if (r.data.events.length === 0) return "nobody in the circle is going to anything coming";
                            return r.data.events
                              .map(
                                (e) =>
                                  `${e.event.title} (${e.event.eventDate.slice(0, 10)}): ${e.goingCount} going — ${e.going
                                    .map((g) => `${g.name}${g.holdsPass ? " (pass)" : ""}`)
                                    .join(", ")}${e.me ? ` · you: ${e.me}` : ""}`,
                              )
                              .join(" | ");
                          })
                        }
                      >
                        Going to
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            await circles.updateCircle(c.id, { name: `${c.name} ` .trim() + " ·" });
                            return "renamed";
                          })
                        }
                      >
                        Rename
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            await circles.deleteCircle(c.id);
                            setOpenId(null);
                            return `deleted "${c.name}"`;
                          })
                        }
                      >
                        Delete
                      </Button>
                    </div>

                    {/* Members: promote and remove. Removing yourself is leaving. */}
                    {c.members?.length ? (
                      <ul className="text-xs text-gray-600 space-y-1">
                        {c.members.map((m) => (
                          <li key={m.userId} className="flex items-center gap-2">
                            <span>{m.user.name}</span>
                            <Badge variant="gray">{m.role}</Badge>
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={() =>
                                run(async () => {
                                  await circles.promoteCircleMember(c.id, m.userId);
                                  return `${m.user.name} promoted`;
                                })
                              }
                            >
                              promote
                            </button>
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={() =>
                                run(async () => {
                                  // An admin steps down; the last one is refused (LAST_ADMIN).
                                  await circles.demoteCircleMember(c.id, m.userId);
                                  return `${m.user.name} is a member again`;
                                })
                              }
                            >
                              step down
                            </button>
                            <button
                              className="text-red-600 hover:underline"
                              onClick={() =>
                                run(async () => {
                                  await circles.removeCircleMember(c.id, m.userId);
                                  return `${m.user.name} removed`;
                                })
                              }
                            >
                              remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <div className="flex gap-2 items-center">
                      <input
                        placeholder="+14085551234"
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                        id={`phone-${c.id}`}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() => {
                          const el = document.getElementById(
                            `phone-${c.id}`,
                          ) as HTMLInputElement | null;
                          const phone = el?.value.trim();
                          if (!phone) return;
                          void run(async () => {
                            await circles.inviteToCircle(c.id, { phone });
                            return `invited ${phone} — a decline will be silent`;
                          });
                        }}
                      >
                        Invite by phone
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-1">Join by link</h2>
        <p className="text-xs text-gray-500 mb-3">
          The preview is public — it works before the viewer has an account,
          which is the point of a link you can paste into a group chat.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            value={joinToken}
            onChange={(e) => setJoinToken(e.target.value)}
            placeholder="token"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <Button
            size="sm"
            variant="secondary"
            isLoading={busy}
            disabled={!joinToken.trim()}
            onClick={() =>
              run(async () => {
                const r = await circles.previewCircleInvite(joinToken.trim());
                return `preview: ${r.data.circle.name} (${r.data.circle.memberCount})`;
              })
            }
          >
            Preview
          </Button>
          <Button
            size="sm"
            isLoading={busy}
            disabled={!joinToken.trim()}
            onClick={() =>
              run(async () => {
                const r = await circles.joinCircleByToken(joinToken.trim());
                return `joined ${r.data.name}`;
              })
            }
          >
            Join
          </Button>
        </div>
      </section>

      {log.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-gray-900 mb-2">What happened</h2>
          <ul className="text-xs text-gray-600 space-y-1 font-mono">
            {log.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
